import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import { Tag, TagProps } from 'shared/components/vxl/Tag/Tag';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

export interface HomeActivatedOfferCardProps {
  offerId: string;
  avatarUrl: string;
  title: string;
  subTitle: string;
  tag?: TagProps;
  onClick: () => void;
  isDisabled?: boolean;
  accessibilityLabel?: string;
  testID?: string;
  accessibilityHint?: string;
}

const HomeActivatedOfferCard = ({
  offerId: _offerId,
  avatarUrl,
  title,
  subTitle: _subTitle,
  tag,
  onClick,
  isDisabled = false,
  accessibilityLabel,
  testID,
  accessibilityHint,
}: HomeActivatedOfferCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const titleStyle = isDisabled ? styles.titleDisabled : styles.title;

  return (
    <View>
      <Pressable
        onPress={isDisabled ? undefined : onClick}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        style={[
          styles.container,
          isFocused && styles.containerFocused,
          isHovered && styles.containerHover,
          isPressed && styles.containerPressed,
          isDisabled && styles.containerDisabled,
        ]}
        disabled={isDisabled}
        accessibilityLabel={accessibilityLabel || `${title} activated offer`}
        accessibilityHint={
          accessibilityHint || 'Double tap to view activated offer details'
        }
        accessibilityRole="button"
        accessibilityState={{
          disabled: isDisabled,
        }}
        testID={testID}
      >
        <View style={styles.contentContainer}>
          <View style={styles.iconSection}>
            <IconAvatar
              variant="SM"
              backgroundColor={theme.color.background.tertiary}
              defaultPlaceholderIcon="STORE"
              imageSource={{ uri: avatarUrl }}
              iconAccessibilityLabel="offer-logo-image"
            />
          </View>

          <View style={styles.textSection}>
            <BodySmText
              style={titleStyle}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {title}
            </BodySmText>
          </View>

          {tag && (
            <View style={styles.tagSection}>
              <Tag {...tag} />
            </View>
          )}
        </View>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.expressive.orchid,
    borderRadius: theme.spacing.xs,
    minHeight: 56,
    overflow: 'hidden',
    width: '100%',
  },
  containerDisabled: {
    opacity: 0.6,
  },
  containerFocused: {
    borderColor: theme.color.expressive.orchid,
    borderWidth: 2,
  },
  containerHover: {
    backgroundColor: theme.color.expressive.orchidHover,
  },
  containerPressed: {
    backgroundColor: theme.color.expressive.orchidPressed,
  },
  contentContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  iconSection: {
    marginRight: theme.spacing.s,
  },
  tagSection: {
    marginLeft: theme.spacing.s,
  },
  textSection: {
    flex: 1,
    justifyContent: 'center',
  },
  title: {
    color: theme.color.text.primary,
  },
  titleDisabled: {
    color: theme.color.interactive.disabled.text,
  },
});

export default HomeActivatedOfferCard;
